import {
  type AtualizarCusto,
  type GravidadeDeAlerta,
  type MudarStatusDoVeiculo,
  type NivelDeBlindagem,
  type OrigemDoCusto,
  type SalvarPlanoDeManutencao,
  type StatusDoVeiculo,
  type TipoDeAlerta,
  type TipoDeCustoDeVeiculo,
  type TipoDeLocal,
  APP_TIMEZONE,
  TAMANHO_MAXIMO_DE_PAGINA,
  formatarPlaca,
  frota,
  gravidadeDoVencimento,
  horariosSchema,
  localDayKey,
} from '@locaveri/shared'
import {
  type QueryClient,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'
import { z } from 'zod'

import { apiDelete, apiGet, apiPatch, apiPost, apiRequest } from '@/lib/api'
import { SEM_VALOR } from '@/lib/datas'
import {
  ROTAS_DE_ARQUIVOS,
  ROTAS_DE_CATEGORIAS,
  ROTAS_DE_CUSTOS,
  ROTAS_DE_LOCAIS,
  ROTAS_DE_VEICULOS,
} from '@/lib/endpoints'
import { montarQuery } from '@/lib/query-string'

/**
 * Frota — categorias, locais, veículos e custos, numa camada de dados só.
 *
 * ---------------------------------------------------------------------------
 * A REGRA DE OURO, NA CAMADA DE DADOS
 * ---------------------------------------------------------------------------
 * O cliente aluga CATEGORIA, nunca modelo. Por isso caução, km incluso, valor
 * do km excedente, idade mínima e tempo de CNH vivem em `categoriaSchema`, e
 * `veiculoSchema` não tem — e não pode ganhar — nenhum campo de preço ou de
 * disponibilidade. O veículo físico só entra na alocação e na operação: placa,
 * chassi, apólice, hodômetro, certificado de blindagem.
 *
 * Se um dia aparecer preço em veículo, a regra foi quebrada aqui, e a Fase 3
 * (tabelas de preço por categoria) vira retrabalho.
 *
 * ---------------------------------------------------------------------------
 * CUSTO: CAIXA × COMPETÊNCIA — AS DUAS ORIGENS NUNCA SÃO SOMADAS
 * ---------------------------------------------------------------------------
 * `LANCAMENTO_MANUAL` é CAIXA: o que saiu do banco naquele mês (o IPVA inteiro
 * em janeiro, a lavagem de ontem). `CALCULO_AUTOMATICO` é COMPETÊNCIA: o que
 * aquele mês consumiu (depreciação, custo de capital, a fatia do overhead e os
 * duodécimos do mesmo IPVA ao longo do ano).
 *
 * O MESMO dinheiro aparece nas duas — é essa a razão de o resumo trazer
 * `caixa` e `competencia` como campos separados e de a listagem trazer `origem`
 * em toda linha. Somar as duas conta o mesmo pagamento duas vezes, e um
 * relatório de rentabilidade errado por um fator de dois é pior do que
 * relatório nenhum. `BadgeDeOrigemDeCusto` existe em `./badges` justamente para
 * que ninguém empilhe as duas colunas sem perceber.
 *
 * ---------------------------------------------------------------------------
 * O QUE VEM DE ONDE
 * ---------------------------------------------------------------------------
 * O modelo de LEITURA é declarado aqui (Zod local), espelhando os DTOs de saída
 * da API — ele é a projeção que o servidor devolve, com categoria e local já
 * resolvidos e as contagens agregadas, e não o contrato de ENTRADA, que é o que
 * `@locaveri/shared` guarda. Os ENUMS, os rótulos e os validadores vêm sempre do
 * pacote compartilhado: string de domínio redigitada é divergência esperando o
 * dia em que alguém acrescenta um status.
 *
 * Dinheiro chega como STRING de centavos (invariante nº 1) e é formatado com
 * `lib/dinheiro`. Nenhuma aritmética monetária acontece no painel.
 */

// ---------------------------------------------------------------------------
// Envelope de listagem
// ---------------------------------------------------------------------------

/**
 * A página que a API devolve. `paginas` vem junto do total de itens — é o
 * servidor que faz a divisão, e refazê-la aqui daria uma resposta diferente na
 * primeira vez que o teto de página mudasse.
 */
function paginaDe<T extends z.ZodType>(item: T) {
  return z.object({
    itens: z.array(item),
    total: z.number().int().nonnegative(),
    pagina: z.number().int().positive(),
    tamanho: z.number().int().positive(),
    paginas: z.number().int().nonnegative(),
  })
}

// ---------------------------------------------------------------------------
// Categoria — o produto
// ---------------------------------------------------------------------------

export const categoriaSchema = z.object({
  id: z.uuid(),
  /** Identificador estável da URL pública e do deep link do app. */
  slug: z.string(),
  nome: z.string(),
  descricao: z.string().nullable(),

  nivelDeBlindagem: frota.nivelDeBlindagemSchema,
  lugares: z.number().int(),
  portas: z.number().int(),
  malas: z.number().int(),
  cambio: frota.tipoDeCambioSchema,

  /** Texto livre ("Compass, Renegade ou similar") — promessa de classe. */
  modelosRepresentativos: z.string(),
  /** Fotos da CATEGORIA. Nunca do veículo específico. */
  galeria: z.array(z.string()),

  idadeMinimaDoCondutor: z.number().int(),
  tempoMinimoDeCnhEmAnos: z.number().int(),

  /** Centavos como string decimal (o wire de `Money`). */
  caucaoBase: z.string(),
  kmDiarioIncluso: z.number().int(),
  /** Centavos como string decimal. */
  valorDoKmExcedente: z.string(),

  /**
   * Parcelamento da venda (Fase 5): quantas parcelas a categoria aceita
   * (1 = à vista) e o piso de cada parcela em centavos (string). O plano
   * determinístico mora em `@locaveri/shared` (`planoDeParcelas`).
   */
  maximoDeParcelas: z.number().int(),
  valorMinimoDaParcela: z.string(),

  /** `null` HERDA da política de custo do tenant — nunca é zero. */
  vidaUtilEmMeses: z.number().int().nullable(),
  residualEmPontosBase: z.number().int().nullable(),

  ordemDeExibicao: z.number().int(),
  ativo: z.boolean(),

  /** Quantos veículos a categoria tem hoje, em qualquer status. */
  veiculos: z.number().int().nonnegative(),

  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Categoria = z.infer<typeof categoriaSchema>

export const contagemPorStatusSchema = z.object({
  status: frota.statusDoVeiculoSchema,
  quantidade: z.number().int().nonnegative(),
})

export type ContagemPorStatus = z.infer<typeof contagemPorStatusSchema>

/**
 * O detalhe traz a distribuição da frota — "tenho oito SUV III-A, mas quantos
 * estão de pé?". `porStatus` vem com TODOS os status, os zerados inclusive e na
 * ordem canônica: grade de tamanho fixo, e "nenhum carro em manutenção" é
 * exatamente a informação que o gestor quer ver escrita.
 */
export const categoriaComFrotaSchema = categoriaSchema.extend({
  porStatus: z.array(contagemPorStatusSchema),
})

export type CategoriaComFrota = z.infer<typeof categoriaComFrotaSchema>

export const paginaDeCategoriasSchema = paginaDe(categoriaSchema)

export type PaginaDeCategorias = z.infer<typeof paginaDeCategoriasSchema>

// ---------------------------------------------------------------------------
// Local — onde o carro é retirado e devolvido
// ---------------------------------------------------------------------------

export const localSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  tipo: frota.tipoDeLocalSchema,

  logradouro: z.string(),
  numero: z.string(),
  complemento: z.string().nullable(),
  bairro: z.string(),
  cidade: z.string(),
  uf: z.string(),
  /** Só dígitos, como está gravado. A máscara é da tela (`formatarCep`). */
  cep: z.string(),

  /**
   * Coordenada é `number` e dinheiro é string, e a assimetria é da API: 7 casas
   * decimais cabem num double sem erro relevante, e todo mapa quer número.
   */
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),

  /** Os sete dias sempre presentes. Dia com lista vazia é FECHADO. */
  horarios: horariosSchema,

  /** Centavos como string decimal. */
  taxaDeEntrega: z.string(),
  taxaDeDevolucao: z.string(),

  ativo: z.boolean(),

  /**
   * Quantos veículos têm este local como base, ou `null`.
   *
   * ⚠️ `null` = quem pediu não é da locadora — a API redige o campo (somado ao
   * logradouro, ele diz quantos blindados dormem naquele endereço). Na prática
   * o painel é operado por papel interno e sempre recebe o número; o
   * `.nullable()` está aqui porque a API e o painel compartilham o mesmo login,
   * e um Zod que estoura é uma tela em branco no lugar de um traço.
   */
  veiculos: z.number().int().nonnegative().nullable(),

  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Local = z.infer<typeof localSchema>

/**
 * A resposta da desativação. `veiculosSemBase` existe porque desativar um ponto
 * tem um efeito colateral que não aparece em lugar nenhum: os carros continuam
 * apontando para uma base que parou de operar, e alguém precisa redistribuí-los.
 */
export const localDesativadoSchema = z.object({
  local: localSchema,
  veiculosSemBase: z.number().int().nonnegative(),
})

export type LocalDesativado = z.infer<typeof localDesativadoSchema>

export const paginaDeLocaisSchema = paginaDe(localSchema)

export type PaginaDeLocais = z.infer<typeof paginaDeLocaisSchema>

// ---------------------------------------------------------------------------
// Veículo — o ativo físico
// ---------------------------------------------------------------------------

/** Recorte da categoria embutido na linha do veículo, para a lista não pedir duas vezes. */
export const categoriaDoVeiculoSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  slug: z.string(),
  nivelDeBlindagem: frota.nivelDeBlindagemSchema,
})

export type CategoriaDoVeiculo = z.infer<typeof categoriaDoVeiculoSchema>

/** A base onde o veículo fica quando ocioso. Nula enquanto não for atribuída. */
export const localDoVeiculoSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  tipo: frota.tipoDeLocalSchema,
})

export type LocalDoVeiculo = z.infer<typeof localDoVeiculoSchema>

/**
 * O veículo como a API o devolve.
 *
 * As datas de vencimento e a de aquisição são `AAAA-MM-DD` puros, sem hora: a
 * coluna é `DATE` e o instante completo faria a tela mostrar um dia a menos em
 * fuso negativo — "o IPVA vence dia 31" virando "dia 30" só se descobre com a
 * multa. Por isso são formatadas com `diasAte`/`formatarData`, nunca com
 * `new Date(...)` direto.
 */
export const veiculoSchema = z.object({
  id: z.uuid(),

  categoryId: z.uuid(),
  categoria: categoriaDoVeiculoSchema,
  locationId: z.uuid().nullable(),
  local: localDoVeiculoSchema.nullable(),

  marca: z.string(),
  modelo: z.string(),
  anoFabricacao: z.number().int(),
  anoModelo: z.number().int(),
  /** Maiúsculas e sem hífen — a forma canônica. A máscara é `formatarPlaca`. */
  placa: z.string(),
  renavam: z.string(),
  chassi: z.string(),
  cor: z.string(),

  certificadoDeBlindagemNumero: z.string().nullable(),
  certificadoDeBlindagemValidade: z.string().nullable(),
  laudoDeBlindagemUrl: z.string().nullable(),

  hodometroAtual: z.number().int().nonnegative(),

  dataDeAquisicao: z.string(),
  /** Centavos como string decimal. */
  valorDeAquisicao: z.string(),
  valorAtualEstimado: z.string().nullable(),

  /** `null` HERDA da categoria (ou da política), nunca zero. */
  vidaUtilEmMeses: z.number().int().nullable(),
  residualEmPontosBase: z.number().int().nullable(),

  seguradora: z.string().nullable(),
  apolice: z.string().nullable(),
  apoliceVencimento: z.string().nullable(),
  ipvaVencimento: z.string().nullable(),
  licenciamentoVencimento: z.string().nullable(),

  statusAtual: frota.statusDoVeiculoSchema,
  prioridadeDeAlocacao: z.number().int(),

  notasInternas: z.string().nullable(),
  /** Preenchida quando o veículo sai da frota. A linha nunca é apagada. */
  dataDeBaixa: z.string().nullable(),

  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Veiculo = z.infer<typeof veiculoSchema>

export const paginaDeVeiculosSchema = paginaDe(veiculoSchema)

export type PaginaDeVeiculos = z.infer<typeof paginaDeVeiculosSchema>

/** O plano de revisão. É 1-1 com o veículo: ou existe um, ou não existe nenhum. */
export const planoDeManutencaoSchema = z.object({
  id: z.uuid(),
  proximaRevisaoKm: z.number().int().nullable(),
  proximaRevisaoData: z.string().nullable(),
  alertaKmAntes: z.number().int().nonnegative(),
  alertaDiasAntes: z.number().int().nonnegative(),
  observacoes: z.string().nullable(),
  ativo: z.boolean(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type PlanoDeManutencao = z.infer<typeof planoDeManutencaoSchema>

/** De onde cada premissa veio na cascata veículo → categoria → política. */
export const ORIGENS_DA_PREMISSA = ['VEICULO', 'CATEGORIA', 'POLITICA'] as const

export type OrigemDaPremissa = (typeof ORIGENS_DA_PREMISSA)[number]

/**
 * O rótulo da herança. "60 meses" e "60 meses herdados da política" são
 * informações diferentes para quem decide se cria um override no veículo.
 */
export const ROTULO_DA_ORIGEM_DA_PREMISSA: Readonly<Record<OrigemDaPremissa, string>> = {
  VEICULO: 'definido no veículo',
  CATEGORIA: 'herdado da categoria',
  POLITICA: 'herdado da política',
}

/**
 * As premissas de depreciação JÁ RESOLVIDAS pela API.
 *
 * O painel não repete a cascata: ela é do motor determinístico do
 * `@locaveri/shared`, e duas implementações da mesma regra divergem no dia em
 * que alguém muda uma delas.
 */
export const premissasDeDepreciacaoSchema = z.object({
  /** Centavos como string decimal. */
  valorDeAquisicao: z.string(),
  vidaUtilEmMeses: z.number().int().positive(),
  residualEmPontosBase: z.number().int().nonnegative(),
  origemDaVidaUtil: z.enum(ORIGENS_DA_PREMISSA),
  origemDoResidual: z.enum(ORIGENS_DA_PREMISSA),
})

export type PremissasDeDepreciacao = z.infer<typeof premissasDeDepreciacaoSchema>

export const veiculoDetalhadoSchema = veiculoSchema.extend({
  planoDeManutencao: planoDeManutencaoSchema.nullable(),
  premissasDeDepreciacao: premissasDeDepreciacaoSchema,
})

export type VeiculoDetalhado = z.infer<typeof veiculoDetalhadoSchema>

/** Uma linha da trilha de status. Append-only: nunca corrigida, nunca apagada. */
export const mudancaDeStatusSchema = z.object({
  id: z.uuid(),
  status: frota.statusDoVeiculoSchema,
  /** Nulo só na primeira linha, a do cadastro. */
  statusAnterior: frota.statusDoVeiculoSchema.nullable(),
  motivo: z.string(),
  /** Nulo quando quem mudou foi o sistema, ou quando o autor foi removido. */
  autor: z.object({ id: z.uuid(), nome: z.string() }).nullable(),
  criadoEm: z.string(),
})

export type MudancaDeStatus = z.infer<typeof mudancaDeStatusSchema>

export const paginaDeMudancasDeStatusSchema = paginaDe(mudancaDeStatusSchema)

export type PaginaDeMudancasDeStatus = z.infer<typeof paginaDeMudancasDeStatusSchema>

// ---------------------------------------------------------------------------
// Alertas de vencimento
// ---------------------------------------------------------------------------

/**
 * Um vencimento próximo ou já vencido.
 *
 * `diasRestantes` é nulo no alerta por quilometragem e `kmRestantes` é nulo em
 * todos os demais — são duas unidades, e converter uma na outra exigiria supor
 * uma média de rodagem que o sistema não tem. Negativo em qualquer um dos dois
 * significa que o limite já passou.
 */
export const alertaDeVencimentoSchema = z.object({
  vehicleId: z.uuid(),
  placa: z.string(),
  marca: z.string(),
  modelo: z.string(),
  categoriaId: z.uuid(),
  categoriaNome: z.string(),

  tipo: z.enum(frota.TIPOS_DE_ALERTA),
  gravidade: z.enum(frota.GRAVIDADES_DE_ALERTA),

  vencimentoEm: z.string().nullable(),
  diasRestantes: z.number().int().nullable(),
  kmRestantes: z.number().int().nullable(),

  /** Número do certificado ou da apólice, quando o alerta tem um. */
  referencia: z.string().nullable(),
})

export type AlertaDeVencimento = z.infer<typeof alertaDeVencimentoSchema>

/**
 * Os alertas e a contagem por gravidade. O `resumo` conta o que está em
 * `itens`, inclusive sob filtro: um total que não bate com a lista embaixo dele
 * é a forma mais rápida de o gestor deixar de confiar no painel.
 */
export const listaDeAlertasSchema = z.object({
  itens: z.array(alertaDeVencimentoSchema),
  resumo: z.object({
    vencidos: z.number().int().nonnegative(),
    criticos: z.number().int().nonnegative(),
    atencao: z.number().int().nonnegative(),
  }),
})

export type ListaDeAlertas = z.infer<typeof listaDeAlertasSchema>

// ---------------------------------------------------------------------------
// Custo
// ---------------------------------------------------------------------------

/**
 * Uma linha de custo. `origem` é o campo mais importante da resposta: é ele que
 * diz se a linha é CAIXA ou COMPETÊNCIA (ver o cabeçalho deste arquivo).
 */
export const custoSchema = z.object({
  id: z.uuid(),
  vehicleId: z.uuid(),
  /** Placa e modelo, para a lista não precisar carregar a frota inteira. */
  veiculo: z.object({
    placa: z.string(),
    marca: z.string(),
    modelo: z.string(),
  }),
  tipo: frota.tipoDeCustoDeVeiculoSchema,
  origem: frota.origemDoCustoSchema,
  /** Centavos como string decimal. */
  valor: z.string(),
  data: z.string(),
  /** Sempre o primeiro dia do mês (`AAAA-MM-01`). */
  competencia: z.string(),
  fornecedor: z.string().nullable(),
  notaFiscal: z.string().nullable(),
  hodometro: z.number().int().nullable(),
  anexoUrl: z.string().nullable(),
  observacoes: z.string().nullable(),
  /** Preenchido a partir da Fase 4, quando o custo nasce de uma locação. */
  reservationId: z.uuid().nullable(),
  /** O lançamento real que originou esta parcela de rateio. */
  custoDeOrigemId: z.uuid().nullable(),
  /** Nulo quando a linha nasceu do fechamento mensal, não de uma pessoa. */
  lancadoPorId: z.uuid().nullable(),
  /** Quantas parcelas de rateio este lançamento gerou. Zero quando não rateado. */
  parcelas: z.number().int().nonnegative(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
})

export type Custo = z.infer<typeof custoSchema>

export const paginaDeCustosSchema = paginaDe(custoSchema)

export type PaginaDeCustos = z.infer<typeof paginaDeCustosSchema>

/**
 * A política de custo indireto do tenant.
 *
 * `definida: false` significa que a API está devolvendo os PADRÕES do banco
 * porque ninguém salvou a tela ainda — a leitura nunca cria linha. A tela
 * precisa dizer isso em voz alta: sugestão não é decisão, e o fechamento recusa
 * rodar sem política salva.
 */
export const politicaDeCustoSchema = z.object({
  definida: z.boolean(),
  vidaUtilEmMeses: z.number().int().positive(),
  residualEmPontosBase: z.number().int().nonnegative(),
  custoDeCapitalAnualEmPontosBase: z.number().int().nonnegative(),
  /** Centavos como string decimal. */
  overheadMensal: z.string(),
  atualizadoEm: z.string().nullable(),
})

export type PoliticaDeCusto = z.infer<typeof politicaDeCustoSchema>

/** Total de uma origem, com a quebra por tipo que a tela usa para explicar. */
export const totalPorOrigemSchema = z.object({
  /** Centavos como string decimal. */
  total: z.string(),
  porTipo: z.array(
    z.object({
      tipo: frota.tipoDeCustoDeVeiculoSchema,
      /** Centavos como string decimal. */
      valor: z.string(),
    }),
  ),
})

export type TotalPorOrigem = z.infer<typeof totalPorOrigemSchema>

/** O custo de um veículo numa competência, com as duas origens LADO A LADO. */
export const linhaDoResumoDeCustoSchema = z.object({
  vehicleId: z.uuid(),
  placa: z.string(),
  marca: z.string(),
  modelo: z.string(),
  caixa: totalPorOrigemSchema,
  competencia: totalPorOrigemSchema,
})

export type LinhaDoResumoDeCusto = z.infer<typeof linhaDoResumoDeCustoSchema>

export const resumoDeCustosSchema = z.object({
  competencia: z.string(),
  itens: z.array(linhaDoResumoDeCustoSchema),
  /** Os dois totais da frota na competência. Também nunca somados entre si. */
  totais: z.object({
    caixa: z.string(),
    competencia: z.string(),
  }),
})

export type ResumoDeCustos = z.infer<typeof resumoDeCustosSchema>

/**
 * O que o fechamento fez. `linhasJaExistentes` é o número que prova a
 * idempotência: rodar de novo na mesma competência devolve tudo aqui e nada em
 * `linhasCriadas`.
 */
export const resultadoDoFechamentoSchema = z.object({
  competencia: z.string(),
  veiculosProcessados: z.number().int().nonnegative(),
  linhasCriadas: z.number().int().nonnegative(),
  linhasJaExistentes: z.number().int().nonnegative(),
  totalPorTipo: z.array(
    z.object({
      tipo: frota.tipoDeCustoDeVeiculoSchema,
      /** Centavos como string decimal. */
      valor: z.string(),
    }),
  ),
})

export type ResultadoDoFechamento = z.infer<typeof resultadoDoFechamentoSchema>

// ---------------------------------------------------------------------------
// Corpos de escrita
// ---------------------------------------------------------------------------

/**
 * Corpo já validado por um schema de `@locaveri/shared`, pronto para o fio.
 *
 * É um mapa aberto pelo mesmo motivo de `CorpoDeConta`: os schemas de entrada
 * TRANSFORMAM (dinheiro vira `bigint`), e `JSON.stringify` de um `bigint`
 * LANÇA. O formulário roda o `safeParse` para validar e envia a ENTRADA
 * validada — strings —, nunca `validado.data`. Tipar aqui como `z.infer` do
 * schema obrigaria uma conversão de tipo no diálogo só para agradar a
 * inferência, e ainda mandaria `bigint` para o `fetch`.
 *
 * Onde o schema NÃO transforma (mudança de status, plano de revisão, correção
 * de custo) o tipo do pacote compartilhado é usado direto — ver as assinaturas
 * das mutações correspondentes.
 */
type CorpoValidado = Readonly<Record<string, unknown>>

export type CorpoDeCategoria = CorpoValidado
export type CorpoDeLocal = CorpoValidado
export type CorpoDeVeiculo = CorpoValidado
export type CorpoDeCusto = CorpoValidado
export type CorpoDaPolitica = CorpoValidado

// ---------------------------------------------------------------------------
// Filtros
// ---------------------------------------------------------------------------

/**
 * Filtro booleano de três estados.
 *
 * É string, e não `boolean | undefined`, porque `montarQuery` descarta `''`
 * mas ENVIA `false` como `"false"` — com booleano não haveria como dizer "tanto
 * faz". `''` = todos, `'true'` = só ativos, `'false'` = só inativos.
 */
export type FiltroDeAtivo = 'true' | 'false' | ''

export interface FiltrosDeCategorias {
  readonly pagina: number
  readonly tamanho: number
  readonly busca: string
  readonly nivelDeBlindagem: NivelDeBlindagem | ''
  readonly ativo: FiltroDeAtivo
}

export interface FiltrosDeLocais {
  readonly pagina: number
  readonly tamanho: number
  readonly busca: string
  readonly tipo: TipoDeLocal | ''
  readonly ativo: FiltroDeAtivo
}

export interface FiltrosDeVeiculos {
  readonly pagina: number
  readonly tamanho: number
  /** Cobre placa, marca e modelo. A placa é normalizada no servidor. */
  readonly busca: string
  readonly categoryId: string
  readonly locationId: string
  readonly status: StatusDoVeiculo | ''
  /** Só os veículos cujo certificado de blindagem vence dentro de N dias. */
  readonly blindagemVencendoEmDias: number | ''
  /** Veículo baixado não aparece por padrão. */
  readonly incluirBaixados: boolean
}

export interface FiltrosDeCustos {
  readonly pagina: number
  readonly tamanho: number
  /** Cobre fornecedor, nota fiscal, observações e a placa do veículo. */
  readonly busca: string
  readonly vehicleId: string
  readonly tipo: TipoDeCustoDeVeiculo | ''
  /** O filtro que separa caixa de competência. Vazio traz as duas — some elas por conta e risco. */
  readonly origem: OrigemDoCusto | ''
  /** Competências inclusive, em `AAAA-MM-01`. */
  readonly competenciaDe: string
  readonly competenciaAte: string
}

export interface FiltrosDeAlertas {
  readonly gravidade: GravidadeDeAlerta | ''
  readonly tipo: TipoDeAlerta | ''
}

// ---------------------------------------------------------------------------
// Chaves de cache
// ---------------------------------------------------------------------------

export const CHAVE_DE_CATEGORIAS = 'categorias'
export const CHAVE_DE_LOCAIS = 'locais'
export const CHAVE_DE_VEICULOS = 'veiculos'
export const CHAVE_DE_CUSTOS = 'custos'
export const CHAVE_DE_ALERTAS = 'alertas-de-vencimento'

/** Página da trilha de status. Vinte linhas cobrem meses de operação de um carro. */
export const TAMANHO_DO_HISTORICO = 20

// ---------------------------------------------------------------------------
// Categorias — leitura
// ---------------------------------------------------------------------------

export function useCategorias(filtros: FiltrosDeCategorias): UseQueryResult<PaginaDeCategorias> {
  return useQuery({
    queryKey: [CHAVE_DE_CATEGORIAS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        busca: filtros.busca,
        nivelDeBlindagem: filtros.nivelDeBlindagem,
        ativo: filtros.ativo,
      })

      return apiGet(`${ROTAS_DE_CATEGORIAS.lista}${query}`, paginaDeCategoriasSchema, { signal })
    },
    // Mantém a página anterior enquanto a nova carrega: sem isso a tabela
    // pisca em branco a cada tecla digitada no filtro.
    placeholderData: (anterior) => anterior,
  })
}

/**
 * Uma categoria com a distribuição da frota por status. Só o detalhe traz
 * `porStatus`: na listagem seriam N agrupamentos por página para desenhar um
 * número que a lista nem mostra.
 */
export function useCategoria(id: string | null): UseQueryResult<CategoriaComFrota> {
  return useQuery({
    queryKey: [CHAVE_DE_CATEGORIAS, id],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_CATEGORIAS.porId(id ?? ''), categoriaComFrotaSchema, { signal }),
    // Sem categoria selecionada não existe pergunta a fazer.
    enabled: id !== null,
  })
}

/**
 * As categorias ativas, para o seletor do formulário de veículo.
 *
 * Uma página só, no teto do servidor: uma locadora tem dezenas de categorias,
 * não milhares. Se um dia passar de `TAMANHO_MAXIMO_DE_PAGINA` o seletor
 * precisa virar campo de busca — e o corte silencioso aqui é o sintoma.
 */
export function useCategoriasAtivas(): UseQueryResult<PaginaDeCategorias> {
  return useQuery({
    queryKey: [CHAVE_DE_CATEGORIAS, 'ativas'],
    queryFn: ({ signal }) => {
      const query = montarQuery({ pagina: 1, tamanho: TAMANHO_MAXIMO_DE_PAGINA, ativo: 'true' })

      return apiGet(`${ROTAS_DE_CATEGORIAS.lista}${query}`, paginaDeCategoriasSchema, { signal })
    },
  })
}

// ---------------------------------------------------------------------------
// Categorias — escrita
// ---------------------------------------------------------------------------

export function useCriarCategoria(): UseMutationResult<Categoria, Error, CorpoDeCategoria> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeCategoria) =>
      apiPost(ROTAS_DE_CATEGORIAS.lista, categoriaSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CATEGORIAS] })
    },
  })
}

export interface AtualizacaoDeCategoria {
  readonly id: string
  /** Já validado pelo schema de atualização de categoria. */
  readonly dados: CorpoDeCategoria
}

export function useAtualizarCategoria(): UseMutationResult<
  Categoria,
  Error,
  AtualizacaoDeCategoria
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeCategoria) =>
      apiPatch(ROTAS_DE_CATEGORIAS.porId(id), categoriaSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CATEGORIAS] })
      // A linha do veículo embute nome e nível de blindagem da categoria:
      // renomear a classe muda a tabela da frota inteira.
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_VEICULOS] })
    },
  })
}

/**
 * Tira a categoria do catálogo. NÃO apaga: a API marca `ativo = false` e recusa
 * com 409 quando há veículo vinculado — nesse caso o caminho é o PATCH.
 */
export function useRemoverCategoria(): UseMutationResult<Categoria, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiDelete(ROTAS_DE_CATEGORIAS.porId(id), categoriaSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CATEGORIAS] })
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_VEICULOS] })
    },
  })
}

// ---------------------------------------------------------------------------
// Galeria da categoria — upload assinado
// ---------------------------------------------------------------------------

/**
 * A resposta de `POST /files/category-photo`, no mesmo formato das demais
 * finalidades de upload.
 *
 * O binário nunca toca a API: ela só ASSINA a URL, e o `PUT` no storage é um
 * `fetch` cru de propósito — o MinIO/S3 não fala o envelope de erro da API, não
 * quer Bearer (mandar o access token para outro host seria vazá-lo) e a
 * assinatura exige EXATAMENTE os cabeçalhos devolvidos, nem um a mais.
 */
const uploadAssinadoSchema = z.object({
  urlDeUpload: z.string(),
  cabecalhos: z.record(z.string(), z.string()),
  urlDoArquivo: z.string(),
  chave: z.string(),
  expiraEmSegundos: z.number().int().positive(),
})

export interface FotoDeCategoriaEnviada {
  /**
   * A CHAVE do storage (`tenants/<uuid>/categorias/<uuid>.<jpg|png|webp>`) — é
   * ELA que vai para `galeria`, e não a URL.
   *
   * O bucket é privado, e é isso que faz a diferença: a vitrine pública serve a
   * foto por REDIRECIONAMENTO ASSINADO a partir da chave, com o bucket fechado.
   * Uma URL de objeto colada no campo simplesmente não abriria — que é o defeito
   * que o `<Textarea>` de URLs produzia sem erro em lugar nenhum.
   */
  readonly chave: string
}

/**
 * Upload em dois passos: a API assina (`POST` autenticado, que é onde o tipo
 * MIME e o teto de 8 MB da finalidade são validados) e o navegador faz o `PUT`
 * direto no storage. O que volta é a chave pronta para o formulário.
 */
export function useEnviarFotoDeCategoria(): UseMutationResult<FotoDeCategoriaEnviada, Error, Blob> {
  return useMutation({
    mutationFn: async (arquivo: Blob) => {
      const assinado = await apiPost(ROTAS_DE_ARQUIVOS.fotoDeCategoria, uploadAssinadoSchema, {
        tipoMime: arquivo.type,
        tamanhoEmBytes: arquivo.size,
      })

      let resposta: Response
      try {
        resposta = await fetch(assinado.urlDeUpload, {
          method: 'PUT',
          headers: assinado.cabecalhos,
          body: arquivo,
        })
      } catch {
        throw new Error(
          'Não foi possível falar com o armazenamento de arquivos. Confira a conexão.',
        )
      }

      if (!resposta.ok) {
        throw new Error(
          `O armazenamento recusou a foto (HTTP ${String(resposta.status)}). Tente de novo.`,
        )
      }

      return { chave: assinado.chave }
    },
  })
}

// ---------------------------------------------------------------------------
// Locais — leitura
// ---------------------------------------------------------------------------

export function useLocais(filtros: FiltrosDeLocais): UseQueryResult<PaginaDeLocais> {
  return useQuery({
    queryKey: [CHAVE_DE_LOCAIS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        busca: filtros.busca,
        tipo: filtros.tipo,
        ativo: filtros.ativo,
      })

      return apiGet(`${ROTAS_DE_LOCAIS.lista}${query}`, paginaDeLocaisSchema, { signal })
    },
    placeholderData: (anterior) => anterior,
  })
}

/** Os locais ativos, para o seletor de base do formulário de veículo. */
export function useLocaisAtivos(): UseQueryResult<PaginaDeLocais> {
  return useQuery({
    queryKey: [CHAVE_DE_LOCAIS, 'ativos'],
    queryFn: ({ signal }) => {
      const query = montarQuery({ pagina: 1, tamanho: TAMANHO_MAXIMO_DE_PAGINA, ativo: 'true' })

      return apiGet(`${ROTAS_DE_LOCAIS.lista}${query}`, paginaDeLocaisSchema, { signal })
    },
  })
}

// ---------------------------------------------------------------------------
// Locais — escrita
// ---------------------------------------------------------------------------

export function useCriarLocal(): UseMutationResult<Local, Error, CorpoDeLocal> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeLocal) => apiPost(ROTAS_DE_LOCAIS.lista, localSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_LOCAIS] })
    },
  })
}

export interface AtualizacaoDeLocal {
  readonly id: string
  /** Já validado pelo schema de atualização de local. */
  readonly dados: CorpoDeLocal
}

export function useAtualizarLocal(): UseMutationResult<Local, Error, AtualizacaoDeLocal> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeLocal) =>
      apiPatch(ROTAS_DE_LOCAIS.porId(id), localSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_LOCAIS] })
      // A linha do veículo embute nome e tipo do local.
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_VEICULOS] })
    },
  })
}

/**
 * Desativa um ponto. Devolve quantos veículos ficaram sem base — a tela precisa
 * mostrar esse número, senão a decisão sai com um efeito colateral invisível.
 */
export function useDesativarLocal(): UseMutationResult<LocalDesativado, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiDelete(ROTAS_DE_LOCAIS.porId(id), localDesativadoSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_LOCAIS] })
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_VEICULOS] })
    },
  })
}

// ---------------------------------------------------------------------------
// Veículos — leitura
// ---------------------------------------------------------------------------

export function useVeiculos(filtros: FiltrosDeVeiculos): UseQueryResult<PaginaDeVeiculos> {
  return useQuery({
    queryKey: [CHAVE_DE_VEICULOS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        busca: filtros.busca,
        categoryId: filtros.categoryId,
        locationId: filtros.locationId,
        status: filtros.status,
        blindagemVencendoEmDias: filtros.blindagemVencendoEmDias,
        incluirBaixados: filtros.incluirBaixados,
      })

      return apiGet(`${ROTAS_DE_VEICULOS.lista}${query}`, paginaDeVeiculosSchema, { signal })
    },
    placeholderData: (anterior) => anterior,
  })
}

/** O detalhe: o mesmo veículo mais o plano de revisão e as premissas resolvidas. */
export function useVeiculo(id: string | null): UseQueryResult<VeiculoDetalhado> {
  return useQuery({
    queryKey: [CHAVE_DE_VEICULOS, id],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_VEICULOS.porId(id ?? ''), veiculoDetalhadoSchema, { signal }),
    enabled: id !== null,
  })
}

/**
 * A trilha de status de um veículo, mais recente primeiro.
 *
 * É append-only: é ela que responde "por que este carro ficou nove dias na
 * funilaria em março". A paginação entra na chave porque cada página é uma
 * resposta diferente do servidor.
 */
export function useHistoricoDeStatus(
  veiculoId: string | null,
  pagina = 1,
): UseQueryResult<PaginaDeMudancasDeStatus> {
  return useQuery({
    queryKey: [CHAVE_DE_VEICULOS, veiculoId, 'historico', pagina],
    queryFn: ({ signal }) => {
      const query = montarQuery({ pagina, tamanho: TAMANHO_DO_HISTORICO })

      return apiGet(
        `${ROTAS_DE_VEICULOS.status(veiculoId ?? '')}${query}`,
        paginaDeMudancasDeStatusSchema,
        { signal },
      )
    },
    enabled: veiculoId !== null,
    placeholderData: (anterior) => anterior,
  })
}

/**
 * Os vencimentos da frota: blindagem, apólice, IPVA, licenciamento e revisão.
 *
 * Não é paginada — a resposta é a lista inteira mais a contagem por gravidade.
 * Nesta fase o vencimento da blindagem é ALERTA: o bloqueio de alocação é da
 * Fase 4.
 */
export function useAlertasDeVencimento(filtros: FiltrosDeAlertas): UseQueryResult<ListaDeAlertas> {
  return useQuery({
    queryKey: [CHAVE_DE_ALERTAS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({ gravidade: filtros.gravidade, tipo: filtros.tipo })

      return apiGet(`${ROTAS_DE_VEICULOS.alertasDeVencimento}${query}`, listaDeAlertasSchema, {
        signal,
      })
    },
    placeholderData: (anterior) => anterior,
  })
}

// ---------------------------------------------------------------------------
// Veículos — escrita
// ---------------------------------------------------------------------------

/**
 * Toda escrita de veículo arrasta categoria, local e alertas.
 *
 * A categoria carrega a contagem de veículos e a distribuição por status; o
 * local carrega quantos carros têm ele como base; o painel de vencimentos é
 * derivado das datas do próprio veículo. Invalidar só a lista deixaria os três
 * mostrando o número de antes até o próximo `staleTime`.
 */
function invalidarFrota(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_VEICULOS] })
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CATEGORIAS] })
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_LOCAIS] })
  void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_ALERTAS] })
}

export function useCriarVeiculo(): UseMutationResult<Veiculo, Error, CorpoDeVeiculo> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeVeiculo) => apiPost(ROTAS_DE_VEICULOS.lista, veiculoSchema, dados),
    onSuccess: () => {
      invalidarFrota(queryClient)
    },
  })
}

export interface AtualizacaoDeVeiculo {
  readonly id: string
  /** Já validado pelo schema de atualização de veículo. */
  readonly dados: CorpoDeVeiculo
}

export function useAtualizarVeiculo(): UseMutationResult<Veiculo, Error, AtualizacaoDeVeiculo> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeVeiculo) =>
      apiPatch(ROTAS_DE_VEICULOS.porId(id), veiculoSchema, dados),
    onSuccess: () => {
      invalidarFrota(queryClient)
    },
  })
}

/**
 * Baixa o veículo da frota. NÃO apaga: a API grava `dataDeBaixa` e move o
 * status para INDISPONIVEL, com a linha de histórico correspondente — custo e
 * trilha continuam apontando para ele.
 */
export function useBaixarVeiculo(): UseMutationResult<Veiculo, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiDelete(ROTAS_DE_VEICULOS.porId(id), veiculoSchema),
    onSuccess: (_resultado, id) => {
      invalidarFrota(queryClient)
      // A baixa também escreve na trilha.
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_VEICULOS, id, 'historico'] })
    },
  })
}

export interface PedidoDeMudancaDeStatus {
  readonly id: string
  /** `MudarStatusDoVeiculo` não transforma nada: aqui vai `validado.data`. */
  readonly dados: MudarStatusDoVeiculo
}

/**
 * Muda o status com motivo obrigatório.
 *
 * A API atualiza `statusAtual` e acrescenta a linha da trilha no MESMO commit,
 * então o cache tem de invalidar as duas coisas — e também os alertas e a
 * categoria, cuja distribuição por status acabou de mudar. Um carro que sai da
 * funilaria e continua contado em "manutenção" na tela da categoria é o tipo de
 * divergência que faz o gestor conferir tudo no WhatsApp de novo.
 */
export function useMudarStatus(): UseMutationResult<Veiculo, Error, PedidoDeMudancaDeStatus> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: PedidoDeMudancaDeStatus) =>
      apiPost(ROTAS_DE_VEICULOS.status(id), veiculoSchema, dados),
    onSuccess: (_resultado, variaveis) => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_VEICULOS] })
      void queryClient.invalidateQueries({
        queryKey: [CHAVE_DE_VEICULOS, variaveis.id, 'historico'],
      })
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_ALERTAS] })
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CATEGORIAS] })
    },
  })
}

export interface PedidoDePlanoDeManutencao {
  readonly veiculoId: string
  /** `SalvarPlanoDeManutencao` não transforma nada: aqui vai `validado.data`. */
  readonly dados: SalvarPlanoDeManutencao
}

/**
 * Salva o plano de revisão (upsert — é 1-1 com o veículo, daí o PUT).
 *
 * Invalida o DETALHE do veículo, e não a listagem: a lista não carrega o plano.
 * Os alertas, sim — revisão por data e por km saem daqui.
 */
export function useSalvarPlanoDeManutencao(): UseMutationResult<
  PlanoDeManutencao,
  Error,
  PedidoDePlanoDeManutencao
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ veiculoId, dados }: PedidoDePlanoDeManutencao) =>
      apiRequest(ROTAS_DE_VEICULOS.planoDeManutencao(veiculoId), {
        schema: planoDeManutencaoSchema,
        method: 'PUT',
        body: dados,
      }),
    onSuccess: (_resultado, variaveis) => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_VEICULOS, variaveis.veiculoId] })
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_ALERTAS] })
    },
  })
}

// ---------------------------------------------------------------------------
// Custos — leitura
// ---------------------------------------------------------------------------

export function useCustos(filtros: FiltrosDeCustos): UseQueryResult<PaginaDeCustos> {
  return useQuery({
    queryKey: [CHAVE_DE_CUSTOS, filtros],
    queryFn: ({ signal }) => {
      const query = montarQuery({
        pagina: filtros.pagina,
        tamanho: filtros.tamanho,
        busca: filtros.busca,
        vehicleId: filtros.vehicleId,
        tipo: filtros.tipo,
        origem: filtros.origem,
        competenciaDe: filtros.competenciaDe,
        competenciaAte: filtros.competenciaAte,
      })

      return apiGet(`${ROTAS_DE_CUSTOS.lista}${query}`, paginaDeCustosSchema, { signal })
    },
    placeholderData: (anterior) => anterior,
  })
}

/** Os parâmetros de custo indireto. Ver `definida` antes de tratar como decisão. */
export function usePoliticaDeCusto(): UseQueryResult<PoliticaDeCusto> {
  return useQuery({
    queryKey: [CHAVE_DE_CUSTOS, 'politica'],
    queryFn: ({ signal }) => apiGet(ROTAS_DE_CUSTOS.politica, politicaDeCustoSchema, { signal }),
  })
}

/**
 * O custo por veículo numa competência (`AAAA-MM-01`), com caixa e competência
 * lado a lado — e nunca somados. Ver o cabeçalho deste arquivo.
 */
export function useResumoDeCustos(competencia: string): UseQueryResult<ResumoDeCustos> {
  return useQuery({
    queryKey: [CHAVE_DE_CUSTOS, 'resumo', competencia],
    queryFn: ({ signal }) => {
      const query = montarQuery({ competencia })

      return apiGet(`${ROTAS_DE_CUSTOS.resumo}${query}`, resumoDeCustosSchema, { signal })
    },
    enabled: competencia.length > 0,
    placeholderData: (anterior) => anterior,
  })
}

// ---------------------------------------------------------------------------
// Custos — escrita
// ---------------------------------------------------------------------------

/**
 * Toda escrita de custo derruba a lista, o resumo e — de carona, porque estão
 * sob a mesma chave — a política, que é uma linha só. Separar em três chaves
 * para poupar esse GET seria otimizar o lado errado.
 */
export function useLancarCusto(): UseMutationResult<Custo, Error, CorpoDeCusto> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDeCusto) => apiPost(ROTAS_DE_CUSTOS.lista, custoSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CUSTOS] })
    },
  })
}

export interface AtualizacaoDeCusto {
  readonly id: string
  /** Só os campos descritivos — `AtualizarCusto` não transforma nada. */
  readonly dados: AtualizarCusto
}

export function useAtualizarCusto(): UseMutationResult<Custo, Error, AtualizacaoDeCusto> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, dados }: AtualizacaoDeCusto) =>
      apiPatch(ROTAS_DE_CUSTOS.porId(id), custoSchema, dados),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CUSTOS] })
    },
  })
}

/**
 * Estorna um lançamento. Aqui a linha SAI mesmo — custo lançado por engano não
 * é fato do passado a preservar, é número errado somando no relatório —, e as
 * parcelas de rateio vão junto. O instantâneo fica no `audit_log`.
 */
export function useEstornarCusto(): UseMutationResult<Custo, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiDelete(ROTAS_DE_CUSTOS.porId(id), custoSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CUSTOS] })
    },
  })
}

/**
 * Salva a política de custo indireto.
 *
 * Invalida também a frota: o detalhe do veículo traz as premissas de
 * depreciação JÁ RESOLVIDAS pela cascata veículo → categoria → política, e
 * mudar a política muda o número que aparece em todo veículo que herda dela.
 */
export function useSalvarPoliticaDeCusto(): UseMutationResult<
  PoliticaDeCusto,
  Error,
  CorpoDaPolitica
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dados: CorpoDaPolitica) =>
      apiRequest(ROTAS_DE_CUSTOS.politica, {
        schema: politicaDeCustoSchema,
        method: 'PUT',
        body: dados,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CUSTOS] })
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_VEICULOS] })
    },
  })
}

/**
 * Roda o fechamento mensal de uma competência (`AAAA-MM-01`).
 *
 * É idempotente: rodar duas vezes no mesmo mês não duplica custo, e a resposta
 * diz quantas linhas já existiam — é esse número que a tela deve mostrar, senão
 * o segundo clique parece não ter feito nada.
 */
export function useFecharCompetencia(): UseMutationResult<ResultadoDoFechamento, Error, string> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (competencia: string) =>
      apiPost(ROTAS_DE_CUSTOS.fechamento, resultadoDoFechamentoSchema, { competencia }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHAVE_DE_CUSTOS] })
    },
  })
}

// ---------------------------------------------------------------------------
// Apresentação
// ---------------------------------------------------------------------------

/**
 * O mínimo para identificar um carro na tela. Estrutural de propósito: serve
 * para `Veiculo`, para a linha do alerta e para o `veiculo` embutido no custo.
 */
export interface VeiculoIdentificavel {
  readonly modelo: string
  readonly placa: string
}

/**
 * `Compass · ABC-1D23`.
 *
 * Modelo E placa, sempre juntos: a frota tem vários Compass, e a placa sozinha
 * não diz nada para quem atende o telefone. A placa sai formatada — no banco
 * ela é canônica (`ABC1D23`), e a operação lê com hífen.
 */
export function identificacaoDoVeiculo(veiculo: VeiculoIdentificavel): string {
  return `${veiculo.modelo} · ${formatarPlaca(veiculo.placa)}`
}

const MILISSEGUNDOS_POR_DIA = 24 * 60 * 60 * 1000
const DATA_SIMPLES = /^\d{4}-\d{2}-\d{2}$/

/**
 * `AAAA-MM-DD` → o mesmo dia à meia-noite UTC, que é onde as contas se somam.
 * `null` quando o formato não bate ou o dia não existe no calendário.
 *
 * O regex é ANCORADO e o dia é conferido contra o calendário de verdade: sem
 * isso, `2026-02-31` passava, o `Date` transbordava para 3 de março em silêncio
 * e a tela dizia "vence em 3 dias" para uma data que não existe — enquanto
 * `formatarData`, sobre o mesmo valor, mostra um traço.
 */
function diaCivilEmUtc(dia: string): number | null {
  if (!DATA_SIMPLES.test(dia)) {
    return null
  }

  const ano = Number(dia.slice(0, 4))
  const mes = Number(dia.slice(5, 7))
  const diaDoMes = Number(dia.slice(8, 10))
  const instante = Date.UTC(ano, mes - 1, diaDoMes)
  const conferencia = new Date(instante)

  return conferencia.getUTCFullYear() === ano &&
    conferencia.getUTCMonth() === mes - 1 &&
    conferencia.getUTCDate() === diaDoMes
    ? instante
    : null
}

/**
 * Dias até uma data `AAAA-MM-DD`. Negativo quando já venceu, `null` quando não
 * há data.
 *
 * A conta é de DIA DE CALENDÁRIO dos dois lados, e não `new Date(valor)`:
 * `new Date('2026-03-15')` é meia-noite UTC, que em São Paulo é 21h do dia 14 —
 * e "vence hoje" viraria "venceu ontem" em toda apólice do país.
 *
 * E "hoje" é o dia em SÃO PAULO, não no fuso do aparelho (invariante nº 2): das
 * 21h à meia-noite o relógio UTC já virou, e o navegador de quem abre o painel
 * de fora do Brasil adiantaria todo vencimento em um dia. O instante entra por
 * parâmetro para que a regra seja testável em qualquer fuso — a máquina de CI
 * roda em UTC, que é exatamente a faixa onde o defeito aparece.
 */
/**
 * "Hoje em São Paulo" memorizado por MINUTO.
 *
 * `localDayKey` monta um `TZDate` e chama o `Intl` — cerca de 15 µs. Parece
 * nada até lembrar quem chama: `gravidadeDaData` roda uma vez por LINHA de
 * tabela e por coluna de vencimento, então uma listagem de 100 veículos custa
 * ~6 ms a cada render, por um valor que muda uma vez por dia. O minuto é a
 * chave porque é grosso o bastante para valer a pena e fino o bastante para a
 * virada da meia-noite aparecer na tela sem recarregar.
 */
let minutoDoCache = -1
let diaDoCache = ''

function hojeNaOperacao(agora: Date): string {
  const minuto = Math.floor(agora.getTime() / 60_000)

  if (minuto !== minutoDoCache) {
    minutoDoCache = minuto
    diaDoCache = localDayKey(agora, APP_TIMEZONE)
  }

  return diaDoCache
}

export function diasAte(data: string | null | undefined, agora: Date = new Date()): number | null {
  if (data === null || data === undefined) {
    return null
  }

  const alvo = diaCivilEmUtc(data)
  const hoje = diaCivilEmUtc(hojeNaOperacao(agora))

  if (alvo === null || hoje === null) {
    return null
  }

  return Math.round((alvo - hoje) / MILISSEGUNDOS_POR_DIA)
}

/**
 * A gravidade de um vencimento pela data, para a coluna que a API não classifica
 * (a listagem de veículos traz as datas cruas; só o painel de alertas traz a
 * gravidade pronta). O corte é o de `@locaveri/shared` — o mesmo do servidor.
 */
export function gravidadeDaData(
  data: string | null | undefined,
  agora: Date = new Date(),
): GravidadeDeAlerta | null {
  const dias = diasAte(data, agora)

  return dias === null ? null : gravidadeDoVencimento(dias)
}

/**
 * A competência do mês corrente, no formato que a API exige (`AAAA-MM-01`).
 *
 * Pelo fuso da operação, e não pelo do aparelho: às 22h do dia 30 o mês em UTC
 * já é o seguinte, e a tela de custos abriria no mês errado — com o fechamento
 * do mês que acabou de terminar sumindo da vista de quem foi conferi-lo.
 */
export function competenciaAtual(agora: Date = new Date()): string {
  return `${localDayKey(agora, APP_TIMEZONE).slice(0, 7)}-01`
}

const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const

/**
 * `2026-03-01` → `março de 2026`.
 *
 * Montado a partir do texto, sem passar por `Date`, pelo mesmo motivo de
 * `diasAte`: `new Date('2026-03-01')` no fuso de São Paulo cai em 28 de
 * fevereiro, e o fechamento de março apareceria como o de fevereiro.
 */
export function rotuloDaCompetencia(competencia: string): string {
  const mes = MESES[Number(competencia.slice(5, 7)) - 1]

  return mes === undefined ? SEM_VALOR : `${mes} de ${competencia.slice(0, 4)}`
}
